//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays, isObjectEmpty } from '@/utils';

//STP Libraries

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
//Assets and Miscellaneous
import { States } from '../../../../../../constants';
import {
  acquisitionTypeOptions,
  constructionTypeOptions,
  dealTypeOptions,
  projectByRightOptions,
  siteControlStatusOptions,
} from './constants';

//Component Begin

export default function ProjectDetails() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    setValue,
    formState: { errors, isValid },
    setFocus,
  } = useFormContext();

  const uploadFile = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: ({ sign, files }: UploadFnReturn) => {
      let signRes: SignedApplicationAssetResponse[] = [];
      if (Array.isArray(sign)) {
        signRes = [...sign];
      } else {
        signRes = [{ ...sign }];
      }

      updateData({
        projectDetails: {
          ...getValues('projectDetails'),
          additionalDocs: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'additionalDocs',
            description: 'Additional Docs',
          })),
        },
      });
      setCurrentStep((prev) => prev + 1);
    },
    onError: (_error: any) => {
      setUploadError('Failed to upload document');
    },
  });

  useEffect(() => {
    if (!isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(get(errors, 'projectDetails'))[0];
      setFocus(`projectDetails.${firstErrorKey}`);
    }
  }, [errors, setFocus]);

  const onSubmit = () => {
    trigger();
    if (!isValid) {
      return;
    }

    const formData = getValues('projectDetails');

    if (formData.selectedFiles?.length) {
      const formDataSelectedFiles = formData?.selectedFiles.map((f: File) => f.name);
      const formContextDataUploadedFiles = formContextData?.projectDetails?.additionalDocs.map((f: File) => f.name);

      const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

      if (fileUploadCompare.array1Diff.length > 0) {
        const signAssetPayload = formData.selectedFiles
          .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
          .map((file: File) => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            formId: String(params.applicationFormId),
          }));
        uploadFile.mutate({
          signAssetPayload,
          files: formData.selectedFiles,
        });
      } else {
        setCurrentStep((prev) => prev + 1);
      }
    } else {
      updateData({
        projectDetails: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Proposed Project Details</h3>
        <p className='mt-1 text-sm text-gray-500'>Details about your proposed project</p>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <HookInput customClassName='col-span-6' label='Street Address' name='projectDetails.streetAddress' type='text' />
        <HookInput customClassName='col-span-6 sm:col-span-6 lg:col-span-2' label='City' name='projectDetails.city' type='text' />
        <HookSelect
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='State / Province'
          name='projectDetails.state'
          options={States.map((s) => ({
            label: s.name,
            value: s.name,
          }))}
        />

        <HookInput
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Zip / Postal Code'
          name='projectDetails.zipCode'
          type='number'
        />
        <HookSelect
          customClassName='col-span-6 sm:col-span-2'
          label='Deal Type'
          name='projectDetails.dealType'
          options={dealTypeOptions.map((d) => ({
            label: d,
            value: d,
          }))}
        />
        <HookMaskedInput
          customClassName='col-span-6 sm:col-span-2'
          label='Proposed Number of Units'
          name='projectDetails.proposedUnits'
          id='proposedUnits'
          maskedComponent='numeric'
        />
        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Is Proposed Project "By-Right"'
          name='projectDetails.projectByRight'
          options={projectByRightOptions.map((v) => ({
            label: v,
            value: v,
          }))}
        />

        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Construction Type'
          name='projectDetails.constructionType'
          options={constructionTypeOptions.map((c) => ({
            label: c,
            value: c,
          }))}
        />

        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Acquisition Type'
          name='projectDetails.acquisitionType'
          options={acquisitionTypeOptions.map((a) => ({
            label: a,
            value: a,
          }))}
        />
        <HookSelect
          customClassName='col-span-6 lg:col-span-2'
          label='Status of Site Control'
          name='projectDetails.siteControlStatus'
          options={siteControlStatusOptions.map((s) => ({
            label: s,
            value: s,
          }))}
        />
        <HookMaskedInput
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Estimated Acquisition Cost'
          name='projectDetails.acquisitionCostEstimate'
          id='acquisitionCostEstimate'
          maskedComponent='currency'
        />
        <HookMaskedInput
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Estimated Development Cost'
          name='projectDetails.developmentCostEstimate'
          id='developmentCostEstimate'
          maskedComponent='currency'
        />
        <HookMaskedInput
          customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
          label='Estimated Pre-Development Cost'
          name='projectDetails.preDevelopmentCostEstimate'
          id='preDevelopmentCostEstimate'
          maskedComponent='currency'
        />
        <div className='col-span-6'>
          <HookTextarea
            name='projectDetails.moreInfo'
            label='More Information About Project'
            placeholder='Please tell us more about this proposed project. Specifically highlight any timing sensitivity.'
            rows={5}
          />
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='projectDetails.selectedFiles'
            label='Additional Docs'
            id='selectedFiles'
            placeholder='Upload Additional Documents'
            multiple
          />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
